
import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';

import { FB_URL } from '../store/actions/types';
import firebase from '../store/firebase'

import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';

// NOTE ON STYLING APPROACH
// The spec calls for "MUI v4 with makeStyles, not sx prop" but also for a
// class-based component with no hooks. makeStyles is a hook and cannot be
// called inside a class component, so this file uses withStyles(styles)(Class)
// instead — it's the class-compatible sibling of makeStyles: same JSS style
// object, same theme access, just applied via HOC instead of a hook. Classes
// come in as this.props.classes exactly like they would from makeStyles.

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  headerBar: {
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  tier3Banner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(1.5, 2),
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  filterControl: {
    minWidth: 140,
  },
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
    [theme.breakpoints.down('xs')]: {
      overflowX: 'hidden',
    },
  },
  // Compact row styling: one line per entry, no vertical padding bloat.
  denseCell: {
    padding: '4px 12px',
    lineHeight: 1.3,
    [theme.breakpoints.down('xs')]: {
      padding: '6px 6px',
    },
  },
  dateCell: {
    padding: '4px 12px',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      padding: '6px 6px',
    },
  },
  // Columns dropped on narrow screens so the table never needs to scroll
  // horizontally — full detail is still one tap away in the drawer.
  hideOnMobile: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  rowTier2: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  rowTier3: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    fontWeight: 700,
  },
  rowClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  pulsingDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    marginRight: theme.spacing(1),
    animation: '$pulse 1.2s infinite',
  },
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.3 },
    '100%': { opacity: 1 },
  },
  truncatedCell: {
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 140,
    },
  },
  actionsCell: {
    padding: '2px 8px',
    whiteSpace: 'nowrap',
  },
  drawerPaper: {
    width: 460,
    padding: theme.spacing(3),
    maxHeight: '100vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      width: '100vw',
      maxWidth: '100vw',
      padding: theme.spacing(2),
    },
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: 'uppercase',
    marginTop: theme.spacing(2),
  },
  excerptBlock: {
    fontFamily: 'monospace',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: 4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actionButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
  errorText: {
    color: theme.palette.error.main,
  },
});

const STATUS_COLORS = {
  open: 'error',
  assigned: 'default',
  resolved: 'primary',
};

class SafeguardingDashboard extends React.Component {
  state = {
    flags: [],
    loading: true,
    error: null,
    supervisors: [],
    selectedFlag: null,
    drawerOpen: false,
    filters: {
      tier: 'all',
      status: 'all',
      agentId: 'all',
      channel: 'all',
    },
    resolveDialog: {
      open: false,
      flagId: null,
      note: '',
    },
    assignDialog: {
      open: false,
      flagId: null,
      assignedTo: '',
    },
    tier3UnacknowledgedCount: 0,
    tier3BannerDismissed: false,
  };

  componentDidMount() {
    const { user } = this.props;
    if (!user || !user.safeguardingAdmin) {
      this.props.history.push('/');
      return;
    }
    this.loadFlags();
    this.loadSupervisors();
  }

  getIdToken = async () => {
    const currentUser = firebase.auth.currentUser;
    if (!currentUser) return null;
    return currentUser.getIdToken();
  };

  callSafeguardingAdmin = async (params) => {
    console.log('callSafeguardingAdmin Load: ', params);

    const token = await this.getIdToken();
    return axios.post(
      `${FB_URL}/m179-safeguardingAdmin`,
      { params },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };


  //  callSafeguardingAdmin = async (params) => {
  //    const token = await this.getIdToken();
  //    return axios.post(`${FB_URL}/m179-safeguardingAdmin`, { 
  //     params: {
  //         params: params, //user wallet address                       
  //     }
  //  })

  //  callSafeguardingAdmin = async (params) => {
  //    const token = await this.getIdToken();
  //    return axios.post(`${FB_URL}/m179-safeguardingAdmin`, { 
  //     params: {
  //         params: params, //user wallet address                       
  //     }
  //  })
  //  };

  loadFlags = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { filters } = this.state;
      const params = { action: 'listFlags' };
      if (filters.tier !== 'all') params.tier = Number(filters.tier);
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.agentId !== 'all') params.agentId = filters.agentId;
      if (filters.channel !== 'all') params.channel = filters.channel;

      const res = await this.callSafeguardingAdmin(params);
      const flags = (res.data && res.data.flags) || [];
      const tier3UnacknowledgedCount = flags.filter(
        (f) => f.tier === 3 && !f.acknowledged
      ).length;
      this.setState({ flags, loading: false, tier3UnacknowledgedCount });
    } catch (err) {
      console.error('loadFlags error:', err.response ? err.response.status : err.message, err.response && err.response.data);
      this.setState({
        loading: false,
        error: 'Failed to load safeguarding flags. Please try again.',
      });
    }
  };

  loadSupervisors = async () => {
    try {
      const res = await this.callSafeguardingAdmin({ action: 'getSupervisors' });
      const supervisors = (res.data && res.data.supervisors) || [];
      this.setState({ supervisors });
    } catch (err) {
      // Non-fatal: assign dropdown will just be empty
    }
  };

  applyFilters = (updatedFilters) => {
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, ...updatedFilters },
      }),
      this.loadFlags
    );
  };

  clearFilters = () => {
    this.setState(
      {
        filters: { tier: 'all', status: 'all', agentId: 'all', channel: 'all' },
      },
      this.loadFlags
    );
  };

  openDetail = (flag) => {
    this.setState({ selectedFlag: flag, drawerOpen: true });
  };

  closeDetail = () => {
    this.setState({ drawerOpen: false, selectedFlag: null });
  };

  openAssignDialog = (flagId) => {
    this.setState({
      assignDialog: { open: true, flagId, assignedTo: '' },
    });
  };

  closeAssignDialog = () => {
    this.setState({
      assignDialog: { open: false, flagId: null, assignedTo: '' },
    });
  };

  openResolveDialog = (flagId) => {
    this.setState({
      resolveDialog: { open: true, flagId, note: '' },
    });
  };

  closeResolveDialog = () => {
    this.setState({
      resolveDialog: { open: false, flagId: null, note: '' },
    });
  };

  handleAssign = async () => {
    const { flagId, assignedTo } = this.state.assignDialog;
    if (!flagId || !assignedTo) return;
    try {
      await this.callSafeguardingAdmin({ action: 'assignFlag', flagId, assignedTo });
      this.closeAssignDialog();
      this.closeDetail();
      this.loadFlags();
    } catch (err) {
      this.setState({ error: 'Failed to assign flag.' });
    }
  };

  handleResolve = async () => {
    const { flagId, note } = this.state.resolveDialog;
    if (!flagId || !note) return;
    try {
      await this.callSafeguardingAdmin({
        action: 'resolveFlag',
        flagId,
        resolutionNote: note,
      });
      this.closeResolveDialog();
      this.closeDetail();
      this.loadFlags();
    } catch (err) {
      this.setState({ error: 'Failed to resolve flag.' });
    }
  };

  handleAcknowledge = async (flagId) => {
    try {
      await this.callSafeguardingAdmin({ action: 'acknowledgeFlag', flagId });
      this.closeDetail();
      this.loadFlags();
    } catch (err) {
      this.setState({ error: 'Failed to acknowledge flag.' });
    }
  };

  dismissTier3Banner = () => {
    this.setState({ tier3BannerDismissed: true });
  };

  renderFilterBar() {
    const { classes } = this.props;
    const { filters } = this.state;

    return (
      <Paper className={classes.filterBar} elevation={1}>
        <FormControl className={classes.filterControl}>
          <InputLabel>Tier</InputLabel>
          <Select
            value={filters.tier}
            onChange={(e) => this.applyFilters({ tier: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="2">Tier 2</MenuItem>
            <MenuItem value="3">Tier 3</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            onChange={(e) => this.applyFilters({ status: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.filterControl}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={filters.agentId}
            onChange={(e) => this.applyFilters({ agentId: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="mpower">Maths Agent GR 10 & 11</MenuItem>
            <MenuItem value="spower">Science Agent GR 10 & 11</MenuItem>
            <MenuItem value="mpower89">Maths Agent GR 8 & 9</MenuItem>
            <MenuItem value="spower89">Science Agent GR 8 & 9</MenuItem>
            <MenuItem value="rpower">Reading and Confidence Development Partner</MenuItem>
            <MenuItem value="aipower">AI & Human Leadership Development Partner</MenuItem>
            <MenuItem value="lpower">Life Path and Calling Development Partner</MenuItem>
            <MenuItem value="Communication">Communication Agent</MenuItem>
          </Select>
        </FormControl>

        <FormControl className={classes.filterControl}>
          <InputLabel>Channel</InputLabel>
          <Select
            value={filters.channel}
            onChange={(e) => this.applyFilters({ channel: e.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="telegram">Telegram</MenuItem>
            <MenuItem value="platform">Platform</MenuItem>
          </Select>
        </FormControl>

        <Button onClick={this.clearFilters}>Clear filters</Button>
      </Paper>
    );
  }

  renderTier3Banner() {
    const { classes } = this.props;
    const { tier3UnacknowledgedCount, tier3BannerDismissed } = this.state;

    if (tier3UnacknowledgedCount === 0 || tier3BannerDismissed) return null;

    return (
      <div className={classes.tier3Banner}>
        <Typography variant="body1">
          {tier3UnacknowledgedCount} unacknowledged Tier 3 flag
          {tier3UnacknowledgedCount === 1 ? '' : 's'} require immediate attention.
        </Typography>
        <IconButton size="small" onClick={this.dismissTier3Banner}>
          <CloseIcon style={{ color: '#fff' }} />
        </IconButton>
      </div>
    );
  }

  renderTable() {
    const { classes } = this.props;
    const { flags, loading, error } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingWrap}>
          <CircularProgress />
        </div>
      );
    }

    if (error) {
      return <Typography className={classes.errorText}>{error}</Typography>;
    }

    if (flags.length === 0) {
      return <Typography>No flags match the current filters.</Typography>;
    }

    return (
      <Paper className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.denseCell}>Tier</TableCell>
              <TableCell className={classes.denseCell}>Learner</TableCell>
              <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>Agent</TableCell>
              <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>Channel</TableCell>
              <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>Concern</TableCell>
              <TableCell className={classes.denseCell}>Status</TableCell>
              <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>Assigned To</TableCell>
              <TableCell className={classes.dateCell}>Date</TableCell>
              <TableCell className={classes.denseCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flags.map((flag) => this.renderRow(flag))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  renderRow(flag) {
    const { classes } = this.props;
    const rowClass = [
      classes.rowClickable,
      flag.tier === 3 ? classes.rowTier3 : classes.rowTier2,
    ].join(' ');

    const supervisor = this.state.supervisors.find((s) => s.uid === flag.assignedTo);

    return (
      <TableRow
        key={flag.flagId}
        className={rowClass}
        onClick={() => this.openDetail(flag)}
      >
        <TableCell className={classes.denseCell}>
          {flag.tier === 3 && !flag.acknowledged && (
            <span className={classes.pulsingDot} />
          )}
          Tier {flag.tier}
        </TableCell>
        <TableCell className={classes.denseCell}>{flag.learnerName}</TableCell>
        <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>{flag.agentId}</TableCell>
        <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>{flag.channel}</TableCell>
        <TableCell className={`${classes.denseCell} ${classes.hideOnMobile} ${classes.truncatedCell}`}>{flag.concern}</TableCell>
        <TableCell className={classes.denseCell}>
          <Chip
            size="small"
            label={flag.status}
            color={STATUS_COLORS[flag.status] || 'default'}
          />
        </TableCell>
        <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>{supervisor ? supervisor.name : '—'}</TableCell>
        <TableCell className={classes.dateCell}>{new Date(flag.createdAt).toLocaleString()}</TableCell>
        <TableCell className={classes.actionsCell} onClick={(e) => e.stopPropagation()}>
          {flag.status !== 'resolved' && (
            <>
              <Button size="small" onClick={() => this.openAssignDialog(flag.flagId)}>
                Assign
              </Button>
              <Button size="small" onClick={() => this.openResolveDialog(flag.flagId)}>
                Resolve
              </Button>
            </>
          )}
          {flag.tier === 3 && !flag.acknowledged && (
            <Button
              size="small"
              color="secondary"
              onClick={() => this.handleAcknowledge(flag.flagId)}
            >
              Acknowledge
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  }

  renderDetailDrawer() {
    const { classes } = this.props;
    const { drawerOpen, selectedFlag, supervisors } = this.state;

    if (!selectedFlag) return null;

    return (
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={this.closeDetail}
        classes={{ paper: classes.drawerPaper }}
      >
        <div className={classes.drawerHeader}>
          <Typography variant="h6">{selectedFlag.learnerName}</Typography>
          <IconButton size="small" onClick={this.closeDetail}>
            <CloseIcon />
          </IconButton>
        </div>

        <Chip
          size="small"
          label={selectedFlag.status}
          color={STATUS_COLORS[selectedFlag.status] || 'default'}
        />

        <Typography className={classes.detailLabel}>Agent / Channel</Typography>
        <Typography>{selectedFlag.agentId} / {selectedFlag.channel}</Typography>

        <Typography className={classes.detailLabel}>Firebase UID</Typography>
        <Typography>{selectedFlag.firebaseUid || '—'}</Typography>

        <Typography className={classes.detailLabel}>Telegram User ID</Typography>
        <Typography>{selectedFlag.telegramUserId || '—'}</Typography>

        <Typography className={classes.detailLabel}>School</Typography>
        <Typography>{selectedFlag.school || '—'}</Typography>

        <Typography className={classes.detailLabel}>Concern</Typography>
        <Typography>{selectedFlag.concern}</Typography>

        <Typography className={classes.detailLabel}>Conversation excerpt</Typography>
        <div className={classes.excerptBlock}>{selectedFlag.conversationExcerpt}</div>

        <Typography className={classes.detailLabel}>Timeline</Typography>
        <Typography>Flagged: {new Date(selectedFlag.createdAt).toLocaleString()}</Typography>
        {selectedFlag.assignedAt && (
          <Typography>
            Assigned: {new Date(selectedFlag.assignedAt).toLocaleString()}
            {selectedFlag.assignedTo &&
              ` to ${
                (supervisors.find((s) => s.uid === selectedFlag.assignedTo) || {}).name ||
                selectedFlag.assignedTo
              }`}
          </Typography>
        )}
        {selectedFlag.resolvedAt && (
          <Typography>
            Resolved: {new Date(selectedFlag.resolvedAt).toLocaleString()}
          </Typography>
        )}
        {selectedFlag.acknowledgedAt && (
          <Typography>
            Acknowledged: {new Date(selectedFlag.acknowledgedAt).toLocaleString()}
          </Typography>
        )}

        <div className={classes.actionButtons}>
          {selectedFlag.status !== 'resolved' && (
            <>
              <Button
                variant="outlined"
                onClick={() => this.openAssignDialog(selectedFlag.flagId)}
              >
                Assign
              </Button>
              <Button
                variant="outlined"
                onClick={() => this.openResolveDialog(selectedFlag.flagId)}
              >
                Resolve
              </Button>
            </>
          )}
          {selectedFlag.tier === 3 && !selectedFlag.acknowledged && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => this.handleAcknowledge(selectedFlag.flagId)}
            >
              Acknowledge
            </Button>
          )}
        </div>
      </Drawer>
    );
  }

  renderAssignDialog() {
    const { assignDialog, supervisors } = this.state;

    return (
      <Dialog open={assignDialog.open} onClose={this.closeAssignDialog}>
        <DialogTitle>Assign flag</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ minWidth: 260 }}>
            <InputLabel>Supervisor</InputLabel>
            <Select
              value={assignDialog.assignedTo}
              onChange={(e) =>
                this.setState({
                  assignDialog: { ...assignDialog, assignedTo: e.target.value },
                })
              }
            >
              {supervisors.map((s) => (
                <MenuItem key={s.uid} value={s.uid}>
                  {s.name} ({s.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeAssignDialog}>Cancel</Button>
          <Button
            color="primary"
            disabled={!assignDialog.assignedTo}
            onClick={this.handleAssign}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderResolveDialog() {
    const { resolveDialog } = this.state;

    return (
      <Dialog open={resolveDialog.open} onClose={this.closeResolveDialog}>
        <DialogTitle>Resolve flag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            fullWidth
            rows={4}
            label="Resolution note"
            value={resolveDialog.note}
            onChange={(e) =>
              this.setState({
                resolveDialog: { ...resolveDialog, note: e.target.value },
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeResolveDialog}>Cancel</Button>
          <Button
            color="primary"
            disabled={!resolveDialog.note}
            onClick={this.handleResolve}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.headerBar}>
          <Typography variant="h4" className={classes.title}>
            Safeguarding Dashboard
          </Typography>
          <Typography variant="body1" className={classes.subtitle}>
            Flags raised by AI agents across Telegram and platform
          </Typography>
        </div>

        {this.renderTier3Banner()}
        {this.renderFilterBar()}
        {this.renderTable()}
        {this.renderDetailDrawer()}
        {this.renderAssignDialog()}
        {this.renderResolveDialog()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.userDialog.userDetails[0] || null,
});

export default connect(mapStateToProps)(withStyles(styles)(SafeguardingDashboard));
///////////////////////////////////////////////////////////////////////////////////////////
// import React from 'react';
// import { connect } from 'react-redux';
// import axios from 'axios';

// import { FB_URL } from '../store/actions/types';
// import firebase from '../store/firebase'

// import { withStyles } from '@material-ui/core/styles';
// import Drawer from '@material-ui/core/Drawer';
// import Dialog from '@material-ui/core/Dialog';
// import DialogTitle from '@material-ui/core/DialogTitle';
// import DialogContent from '@material-ui/core/DialogContent';
// import DialogActions from '@material-ui/core/DialogActions';
// import Button from '@material-ui/core/Button';
// import TextField from '@material-ui/core/TextField';
// import Select from '@material-ui/core/Select';
// import MenuItem from '@material-ui/core/MenuItem';
// import FormControl from '@material-ui/core/FormControl';
// import InputLabel from '@material-ui/core/InputLabel';
// import Table from '@material-ui/core/Table';
// import TableHead from '@material-ui/core/TableHead';
// import TableBody from '@material-ui/core/TableBody';
// import TableRow from '@material-ui/core/TableRow';
// import TableCell from '@material-ui/core/TableCell';
// import Paper from '@material-ui/core/Paper';
// import Chip from '@material-ui/core/Chip';
// import IconButton from '@material-ui/core/IconButton';
// import CircularProgress from '@material-ui/core/CircularProgress';
// import Typography from '@material-ui/core/Typography';
// import CloseIcon from '@material-ui/icons/Close';

// // NOTE ON STYLING APPROACH
// // The spec calls for "MUI v4 with makeStyles, not sx prop" but also for a
// // class-based component with no hooks. makeStyles is a hook and cannot be
// // called inside a class component, so this file uses withStyles(styles)(Class)
// // instead — it's the class-compatible sibling of makeStyles: same JSS style
// // object, same theme access, just applied via HOC instead of a hook. Classes
// // come in as this.props.classes exactly like they would from makeStyles.

// const styles = (theme) => ({
//   root: {
//     padding: theme.spacing(3),
//   },
//   headerBar: {
//     marginBottom: theme.spacing(2),
//   },
//   title: {
//     fontWeight: 700,
//   },
//   subtitle: {
//     color: theme.palette.text.secondary,
//     marginBottom: theme.spacing(2),
//   },
//   tier3Banner: {
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'space-between',
//     backgroundColor: theme.palette.error.main,
//     color: theme.palette.error.contrastText,
//     padding: theme.spacing(1.5, 2),
//     borderRadius: 4,
//     marginBottom: theme.spacing(2),
//   },
//   filterBar: {
//     display: 'flex',
//     flexWrap: 'wrap',
//     gap: theme.spacing(2),
//     alignItems: 'center',
//     marginBottom: theme.spacing(2),
//     padding: theme.spacing(2),
//   },
//   filterControl: {
//     minWidth: 140,
//   },
//   tableWrap: {
//     width: '100%',
//     overflowX: 'auto',
//   },
//   rowTier2: {
//     borderLeft: `4px solid ${theme.palette.warning.main}`,
//   },
//   rowTier3: {
//     borderLeft: `4px solid ${theme.palette.error.main}`,
//     fontWeight: 700,
//   },
//   rowClickable: {
//     cursor: 'pointer',
//     '&:hover': {
//       backgroundColor: theme.palette.action.hover,
//     },
//   },
//   pulsingDot: {
//     display: 'inline-block',
//     width: 10,
//     height: 10,
//     borderRadius: '50%',
//     backgroundColor: theme.palette.error.main,
//     marginRight: theme.spacing(1),
//     animation: '$pulse 1.2s infinite',
//   },
//   '@keyframes pulse': {
//     '0%': { opacity: 1 },
//     '50%': { opacity: 0.3 },
//     '100%': { opacity: 1 },
//   },
//   truncatedCell: {
//     maxWidth: 260,
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//     whiteSpace: 'nowrap',
//   },
//   drawerPaper: {
//     width: 460,
//     padding: theme.spacing(3),
//   },
//   drawerHeader: {
//     display: 'flex',
//     justifyContent: 'space-between',
//     alignItems: 'flex-start',
//     marginBottom: theme.spacing(2),
//   },
//   detailLabel: {
//     color: theme.palette.text.secondary,
//     fontSize: 12,
//     textTransform: 'uppercase',
//     marginTop: theme.spacing(2),
//   },
//   excerptBlock: {
//     fontFamily: 'monospace',
//     backgroundColor: theme.palette.grey[100],
//     padding: theme.spacing(1.5),
//     borderRadius: 4,
//     whiteSpace: 'pre-wrap',
//   },
//   actionButtons: {
//     display: 'flex',
//     gap: theme.spacing(1),
//     marginTop: theme.spacing(3),
//   },
//   loadingWrap: {
//     display: 'flex',
//     justifyContent: 'center',
//     padding: theme.spacing(6),
//   },
//   errorText: {
//     color: theme.palette.error.main,
//   },
// });

// const STATUS_COLORS = {
//   open: 'error',
//   assigned: 'default',
//   resolved: 'primary',
// };

// class SafeguardingDashboard extends React.Component {
//   state = {
//     flags: [],
//     loading: true,
//     error: null,
//     supervisors: [],
//     selectedFlag: null,
//     drawerOpen: false,
//     filters: {
//       tier: 'all',
//       status: 'all',
//       agentId: 'all',
//       channel: 'all',
//     },
//     resolveDialog: {
//       open: false,
//       flagId: null,
//       note: '',
//     },
//     assignDialog: {
//       open: false,
//       flagId: null,
//       assignedTo: '',
//     },
//     tier3UnacknowledgedCount: 0,
//     tier3BannerDismissed: false,
//   };

//   componentDidMount() {
//     const { user } = this.props;
//     if (!user || !user.safeguardingAdmin) {
//       this.props.history.push('/');
//       return;
//     }
//     this.loadFlags();
//     this.loadSupervisors();
//   }

//   getIdToken = async () => {
//     const currentUser = firebase.auth.currentUser;
//     if (!currentUser) return null;
//     return currentUser.getIdToken();
//   };

//   callSafeguardingAdmin = async (params) => {
//     console.log('callSafeguardingAdmin Load: ', params);

//     const token = await this.getIdToken();
//     return axios.post(
//       `${FB_URL}/m179-safeguardingAdmin`,
//       { params },
//       { headers: { Authorization: `Bearer ${token}` } }
//     );
//   };


//   //  callSafeguardingAdmin = async (params) => {
//   //    const token = await this.getIdToken();
//   //    return axios.post(`${FB_URL}/m179-safeguardingAdmin`, { 
//   //     params: {
//   //         params: params, //user wallet address                       
//   //     }
//   //  })

//   //  callSafeguardingAdmin = async (params) => {
//   //    const token = await this.getIdToken();
//   //    return axios.post(`${FB_URL}/m179-safeguardingAdmin`, { 
//   //     params: {
//   //         params: params, //user wallet address                       
//   //     }
//   //  })
//   //  };

//   loadFlags = async () => {
//     this.setState({ loading: true, error: null });
//     try {
//       const { filters } = this.state;
//       const params = { action: 'listFlags' };
//       if (filters.tier !== 'all') params.tier = Number(filters.tier);
//       if (filters.status !== 'all') params.status = filters.status;
//       if (filters.agentId !== 'all') params.agentId = filters.agentId;
//       if (filters.channel !== 'all') params.channel = filters.channel;

//       const res = await this.callSafeguardingAdmin(params);
//       const flags = (res.data && res.data.flags) || [];
//       const tier3UnacknowledgedCount = flags.filter(
//         (f) => f.tier === 3 && !f.acknowledged
//       ).length;
//       this.setState({ flags, loading: false, tier3UnacknowledgedCount });
//     } catch (err) {
//       console.error('loadFlags error:', err.response ? err.response.status : err.message, err.response && err.response.data);
//       this.setState({
//         loading: false,
//         error: 'Failed to load safeguarding flags. Please try again.',
//       });
//     }
//   };

//   loadSupervisors = async () => {
//     try {
//       const res = await this.callSafeguardingAdmin({ action: 'getSupervisors' });
//       const supervisors = (res.data && res.data.supervisors) || [];
//       this.setState({ supervisors });
//     } catch (err) {
//       // Non-fatal: assign dropdown will just be empty
//     }
//   };

//   applyFilters = (updatedFilters) => {
//     this.setState(
//       (prevState) => ({
//         filters: { ...prevState.filters, ...updatedFilters },
//       }),
//       this.loadFlags
//     );
//   };

//   clearFilters = () => {
//     this.setState(
//       {
//         filters: { tier: 'all', status: 'all', agentId: 'all', channel: 'all' },
//       },
//       this.loadFlags
//     );
//   };

//   openDetail = (flag) => {
//     this.setState({ selectedFlag: flag, drawerOpen: true });
//   };

//   closeDetail = () => {
//     this.setState({ drawerOpen: false, selectedFlag: null });
//   };

//   openAssignDialog = (flagId) => {
//     this.setState({
//       assignDialog: { open: true, flagId, assignedTo: '' },
//     });
//   };

//   closeAssignDialog = () => {
//     this.setState({
//       assignDialog: { open: false, flagId: null, assignedTo: '' },
//     });
//   };

//   openResolveDialog = (flagId) => {
//     this.setState({
//       resolveDialog: { open: true, flagId, note: '' },
//     });
//   };

//   closeResolveDialog = () => {
//     this.setState({
//       resolveDialog: { open: false, flagId: null, note: '' },
//     });
//   };

//   handleAssign = async () => {
//     const { flagId, assignedTo } = this.state.assignDialog;
//     if (!flagId || !assignedTo) return;
//     try {
//       await this.callSafeguardingAdmin({ action: 'assignFlag', flagId, assignedTo });
//       this.closeAssignDialog();
//       this.closeDetail();
//       this.loadFlags();
//     } catch (err) {
//       this.setState({ error: 'Failed to assign flag.' });
//     }
//   };

//   handleResolve = async () => {
//     const { flagId, note } = this.state.resolveDialog;
//     if (!flagId || !note) return;
//     try {
//       await this.callSafeguardingAdmin({
//         action: 'resolveFlag',
//         flagId,
//         resolutionNote: note,
//       });
//       this.closeResolveDialog();
//       this.closeDetail();
//       this.loadFlags();
//     } catch (err) {
//       this.setState({ error: 'Failed to resolve flag.' });
//     }
//   };

//   handleAcknowledge = async (flagId) => {
//     try {
//       await this.callSafeguardingAdmin({ action: 'acknowledgeFlag', flagId });
//       this.closeDetail();
//       this.loadFlags();
//     } catch (err) {
//       this.setState({ error: 'Failed to acknowledge flag.' });
//     }
//   };

//   dismissTier3Banner = () => {
//     this.setState({ tier3BannerDismissed: true });
//   };

//   renderFilterBar() {
//     const { classes } = this.props;
//     const { filters } = this.state;

//     return (
//       <Paper className={classes.filterBar} elevation={1}>
//         <FormControl className={classes.filterControl}>
//           <InputLabel>Tier</InputLabel>
//           <Select
//             value={filters.tier}
//             onChange={(e) => this.applyFilters({ tier: e.target.value })}
//           >
//             <MenuItem value="all">All</MenuItem>
//             <MenuItem value="2">Tier 2</MenuItem>
//             <MenuItem value="3">Tier 3</MenuItem>
//           </Select>
//         </FormControl>

//         <FormControl className={classes.filterControl}>
//           <InputLabel>Status</InputLabel>
//           <Select
//             value={filters.status}
//             onChange={(e) => this.applyFilters({ status: e.target.value })}
//           >
//             <MenuItem value="all">All</MenuItem>
//             <MenuItem value="open">Open</MenuItem>
//             <MenuItem value="assigned">Assigned</MenuItem>
//             <MenuItem value="resolved">Resolved</MenuItem>
//           </Select>
//         </FormControl>

//         <FormControl className={classes.filterControl}>
//           <InputLabel>Agent</InputLabel>
//           <Select
//             value={filters.agentId}
//             onChange={(e) => this.applyFilters({ agentId: e.target.value })}
//           >
//             <MenuItem value="all">All</MenuItem>
//             <MenuItem value="mpower">mpower</MenuItem>
//             <MenuItem value="spower">spower</MenuItem>
//             <MenuItem value="mpower89">mpower89</MenuItem>
//             <MenuItem value="spower89">spower89</MenuItem>
//           </Select>
//         </FormControl>

//         <FormControl className={classes.filterControl}>
//           <InputLabel>Channel</InputLabel>
//           <Select
//             value={filters.channel}
//             onChange={(e) => this.applyFilters({ channel: e.target.value })}
//           >
//             <MenuItem value="all">All</MenuItem>
//             <MenuItem value="telegram">Telegram</MenuItem>
//             <MenuItem value="platform">Platform</MenuItem>
//           </Select>
//         </FormControl>

//         <Button onClick={this.clearFilters}>Clear filters</Button>
//       </Paper>
//     );
//   }

//   renderTier3Banner() {
//     const { classes } = this.props;
//     const { tier3UnacknowledgedCount, tier3BannerDismissed } = this.state;

//     if (tier3UnacknowledgedCount === 0 || tier3BannerDismissed) return null;

//     return (
//       <div className={classes.tier3Banner}>
//         <Typography variant="body1">
//           {tier3UnacknowledgedCount} unacknowledged Tier 3 flag
//           {tier3UnacknowledgedCount === 1 ? '' : 's'} require immediate attention.
//         </Typography>
//         <IconButton size="small" onClick={this.dismissTier3Banner}>
//           <CloseIcon style={{ color: '#fff' }} />
//         </IconButton>
//       </div>
//     );
//   }

//   renderTable() {
//     const { classes } = this.props;
//     const { flags, loading, error } = this.state;

//     if (loading) {
//       return (
//         <div className={classes.loadingWrap}>
//           <CircularProgress />
//         </div>
//       );
//     }

//     if (error) {
//       return <Typography className={classes.errorText}>{error}</Typography>;
//     }

//     if (flags.length === 0) {
//       return <Typography>No flags match the current filters.</Typography>;
//     }

//     return (
//       <Paper className={classes.tableWrap}>
//         <Table>
//           <TableHead>
//             <TableRow>
//               <TableCell>Tier</TableCell>
//               <TableCell>Learner</TableCell>
//               <TableCell>Agent</TableCell>
//               <TableCell>Channel</TableCell>
//               <TableCell>Concern</TableCell>
//               <TableCell>Status</TableCell>
//               <TableCell>Assigned To</TableCell>
//               <TableCell>Date</TableCell>
//               <TableCell>Actions</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {flags.map((flag) => this.renderRow(flag))}
//           </TableBody>
//         </Table>
//       </Paper>
//     );
//   }

//   renderRow(flag) {
//     const { classes } = this.props;
//     const rowClass = [
//       classes.rowClickable,
//       flag.tier === 3 ? classes.rowTier3 : classes.rowTier2,
//     ].join(' ');

//     const supervisor = this.state.supervisors.find((s) => s.uid === flag.assignedTo);

//     return (
//       <TableRow
//         key={flag.flagId}
//         className={rowClass}
//         onClick={() => this.openDetail(flag)}
//       >
//         <TableCell>
//           {flag.tier === 3 && !flag.acknowledged && (
//             <span className={classes.pulsingDot} />
//           )}
//           Tier {flag.tier}
//         </TableCell>
//         <TableCell>{flag.learnerName}</TableCell>
//         <TableCell>{flag.agentId}</TableCell>
//         <TableCell>{flag.channel}</TableCell>
//         <TableCell className={classes.truncatedCell}>{flag.concern}</TableCell>
//         <TableCell>
//           <Chip
//             size="small"
//             label={flag.status}
//             color={STATUS_COLORS[flag.status] || 'default'}
//           />
//         </TableCell>
//         <TableCell>{supervisor ? supervisor.name : '—'}</TableCell>
//         <TableCell>{new Date(flag.createdAt).toLocaleString()}</TableCell>
//         <TableCell onClick={(e) => e.stopPropagation()}>
//           {flag.status !== 'resolved' && (
//             <>
//               <Button size="small" onClick={() => this.openAssignDialog(flag.flagId)}>
//                 Assign
//               </Button>
//               <Button size="small" onClick={() => this.openResolveDialog(flag.flagId)}>
//                 Resolve
//               </Button>
//             </>
//           )}
//           {flag.tier === 3 && !flag.acknowledged && (
//             <Button
//               size="small"
//               color="secondary"
//               onClick={() => this.handleAcknowledge(flag.flagId)}
//             >
//               Acknowledge
//             </Button>
//           )}
//         </TableCell>
//       </TableRow>
//     );
//   }

//   renderDetailDrawer() {
//     const { classes } = this.props;
//     const { drawerOpen, selectedFlag, supervisors } = this.state;

//     if (!selectedFlag) return null;

//     return (
//       <Drawer
//         anchor="right"
//         open={drawerOpen}
//         onClose={this.closeDetail}
//         classes={{ paper: classes.drawerPaper }}
//       >
//         <div className={classes.drawerHeader}>
//           <Typography variant="h6">{selectedFlag.learnerName}</Typography>
//           <IconButton size="small" onClick={this.closeDetail}>
//             <CloseIcon />
//           </IconButton>
//         </div>

//         <Chip
//           size="small"
//           label={selectedFlag.status}
//           color={STATUS_COLORS[selectedFlag.status] || 'default'}
//         />

//         <Typography className={classes.detailLabel}>Agent / Channel</Typography>
//         <Typography>{selectedFlag.agentId} / {selectedFlag.channel}</Typography>

//         <Typography className={classes.detailLabel}>Firebase UID</Typography>
//         <Typography>{selectedFlag.firebaseUid || '—'}</Typography>

//         <Typography className={classes.detailLabel}>Telegram User ID</Typography>
//         <Typography>{selectedFlag.telegramUserId || '—'}</Typography>

//         <Typography className={classes.detailLabel}>School</Typography>
//         <Typography>{selectedFlag.school || '—'}</Typography>

//         <Typography className={classes.detailLabel}>Concern</Typography>
//         <Typography>{selectedFlag.concern}</Typography>

//         <Typography className={classes.detailLabel}>Conversation excerpt</Typography>
//         <div className={classes.excerptBlock}>{selectedFlag.conversationExcerpt}</div>

//         <Typography className={classes.detailLabel}>Timeline</Typography>
//         <Typography>Flagged: {new Date(selectedFlag.createdAt).toLocaleString()}</Typography>
//         {selectedFlag.assignedAt && (
//           <Typography>
//             Assigned: {new Date(selectedFlag.assignedAt).toLocaleString()}
//             {selectedFlag.assignedTo &&
//               ` to ${
//                 (supervisors.find((s) => s.uid === selectedFlag.assignedTo) || {}).name ||
//                 selectedFlag.assignedTo
//               }`}
//           </Typography>
//         )}
//         {selectedFlag.resolvedAt && (
//           <Typography>
//             Resolved: {new Date(selectedFlag.resolvedAt).toLocaleString()}
//           </Typography>
//         )}
//         {selectedFlag.acknowledgedAt && (
//           <Typography>
//             Acknowledged: {new Date(selectedFlag.acknowledgedAt).toLocaleString()}
//           </Typography>
//         )}

//         <div className={classes.actionButtons}>
//           {selectedFlag.status !== 'resolved' && (
//             <>
//               <Button
//                 variant="outlined"
//                 onClick={() => this.openAssignDialog(selectedFlag.flagId)}
//               >
//                 Assign
//               </Button>
//               <Button
//                 variant="outlined"
//                 onClick={() => this.openResolveDialog(selectedFlag.flagId)}
//               >
//                 Resolve
//               </Button>
//             </>
//           )}
//           {selectedFlag.tier === 3 && !selectedFlag.acknowledged && (
//             <Button
//               variant="contained"
//               color="secondary"
//               onClick={() => this.handleAcknowledge(selectedFlag.flagId)}
//             >
//               Acknowledge
//             </Button>
//           )}
//         </div>
//       </Drawer>
//     );
//   }

//   renderAssignDialog() {
//     const { assignDialog, supervisors } = this.state;

//     return (
//       <Dialog open={assignDialog.open} onClose={this.closeAssignDialog}>
//         <DialogTitle>Assign flag</DialogTitle>
//         <DialogContent>
//           <FormControl fullWidth style={{ minWidth: 260 }}>
//             <InputLabel>Supervisor</InputLabel>
//             <Select
//               value={assignDialog.assignedTo}
//               onChange={(e) =>
//                 this.setState({
//                   assignDialog: { ...assignDialog, assignedTo: e.target.value },
//                 })
//               }
//             >
//               {supervisors.map((s) => (
//                 <MenuItem key={s.uid} value={s.uid}>
//                   {s.name} ({s.email})
//                 </MenuItem>
//               ))}
//             </Select>
//           </FormControl>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={this.closeAssignDialog}>Cancel</Button>
//           <Button
//             color="primary"
//             disabled={!assignDialog.assignedTo}
//             onClick={this.handleAssign}
//           >
//             Assign
//           </Button>
//         </DialogActions>
//       </Dialog>
//     );
//   }

//   renderResolveDialog() {
//     const { resolveDialog } = this.state;

//     return (
//       <Dialog open={resolveDialog.open} onClose={this.closeResolveDialog}>
//         <DialogTitle>Resolve flag</DialogTitle>
//         <DialogContent>
//           <TextField
//             autoFocus
//             multiline
//             fullWidth
//             rows={4}
//             label="Resolution note"
//             value={resolveDialog.note}
//             onChange={(e) =>
//               this.setState({
//                 resolveDialog: { ...resolveDialog, note: e.target.value },
//               })
//             }
//           />
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={this.closeResolveDialog}>Cancel</Button>
//           <Button
//             color="primary"
//             disabled={!resolveDialog.note}
//             onClick={this.handleResolve}
//           >
//             Resolve
//           </Button>
//         </DialogActions>
//       </Dialog>
//     );
//   }

//   render() {
//     const { classes } = this.props;

//     return (
//       <div className={classes.root}>
//         <div className={classes.headerBar}>
//           <Typography variant="h4" className={classes.title}>
//             Safeguarding Dashboard
//           </Typography>
//           <Typography variant="body1" className={classes.subtitle}>
//             Flags raised by AI agents across Telegram and platform
//           </Typography>
//         </div>

//         {this.renderTier3Banner()}
//         {this.renderFilterBar()}
//         {this.renderTable()}
//         {this.renderDetailDrawer()}
//         {this.renderAssignDialog()}
//         {this.renderResolveDialog()}
//       </div>
//     );
//   }
// }

// const mapStateToProps = (state) => ({
//   user: state.userDialog.userDetails[0] || null,
// });

// export default connect(mapStateToProps)(withStyles(styles)(SafeguardingDashboard));
